import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { getAuth, hasUsedProTrial } from '../auth';
import logoImg from '../logo.png';

/* ═══════════════════════════════════════════════════════════════════
   DESIGN TOKENS
   ═══════════════════════════════════════════════════════════════════ */
const GRAD = 'linear-gradient(135deg, #00E633 0%, #C8FF00 100%)';
const GRAD_CTA = 'linear-gradient(135deg, #00E633, #C8FF00)';
const GRAD_PRO = 'linear-gradient(135deg, #00E633, #C8FF00)';
const GRAD_ELITE = 'linear-gradient(135deg, #009BFF, #0052CC)';
const gradText = { background: GRAD, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' };
const proText = { background: GRAD_PRO, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' };
const eliteText = { background: GRAD_ELITE, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' };
const F = { fontFamily: "'Barlow', sans-serif" };

/* ═══════════════════════════════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════════════════════════════ */
function Home() {
  /* ── state (preserved) ── */
  const [isAnual, setIsAnual] = useState(true);
  const [proPrice, setProPrice] = useState('49');
  const [elitePrice, setElitePrice] = useState('97');
  const [proPeriod, setProPeriod] = useState('/mês · cobrado anualmente');
  const [elitePeriod, setElitePeriod] = useState('/mês · cobrado anualmente');
  const [activeTab, setActiveTab] = useState('coach');
  const [openFaq, setOpenFaq] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isLogado, setIsLogado] = useState(() => !!getAuth()?.token);
  const [authSnapshot, setAuthSnapshot] = useState(() => getAuth());
  const videoRef = useRef(null);
  const ctaVideoRef = useRef(null);
  const metricsRef = useRef(null);
  const usedProTrial = hasUsedProTrial(authSnapshot?.user?.email);
  const proPrimaryCta = isLogado && usedProTrial ? 'Assinar Pro' : 'Testar grátis por 7 dias';
  const proPrimaryTo = isLogado && usedProTrial ? '/assinatura#planos' : '/cadastro';

  /* ── handlers (preserved) ── */
  function toggleBilling() {
    const next = !isAnual;
    setIsAnual(next);
    setProPrice(next ? '49' : '69');
    setElitePrice(next ? '97' : '129');
    setProPeriod(next ? '/mês · cobrado anualmente' : '/mês · cobrado mensalmente');
    setElitePeriod(next ? '/mês · cobrado anualmente' : '/mês · cobrado mensalmente');
  }

  function toggleFaq(index) {
    setOpenFaq(openFaq === index ? null : index);
  }

  function handleLogoClick() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  /* ── effects (preserved) ── */
  useEffect(() => {
    const syncAuth = () => {
      const nextAuth = getAuth();
      setAuthSnapshot(nextAuth);
      setIsLogado(!!nextAuth?.token);
    };
    const onScroll = () => { setIsScrolled(window.scrollY > 60); };
    syncAuth();
    onScroll();
    window.addEventListener('scroll', onScroll);
    window.addEventListener('storage', syncAuth);
    window.addEventListener('pm-auth-changed', syncAuth);
    window.addEventListener('pm-trial-changed', syncAuth);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('storage', syncAuth);
      window.removeEventListener('pm-auth-changed', syncAuth);
      window.removeEventListener('pm-trial-changed', syncAuth);
    };
  }, []);

  useEffect(() => {
    // HLS video (preserved exactly)
    const video = videoRef.current;
    if (!video) return;
    const src = 'https://stream.mux.com/NcXMgE2lezVoFkMEx00N6nPyFZa4PiQ01hQby7XuNCZr4.m3u8';
    if (window.Hls && window.Hls.isSupported()) {
      const hls = new window.Hls();
      hls.loadSource(src);
      hls.attachMedia(video);
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = src;
    }
  }, []);

  useEffect(() => {
    // HLS video for CTA section
    const video = ctaVideoRef.current;
    if (!video) return;
    const src = 'https://stream.mux.com/uLatxY5nTkuh8osRbH3A1Zg79Yj2NdwtqULvMogx7OE.m3u8';
    if (window.Hls && window.Hls.isSupported()) {
      const hls = new window.Hls();
      hls.loadSource(src);
      hls.attachMedia(video);
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = src;
    }
  }, []);

  useEffect(() => {
    // Metrics table (preserved, but ref element not rendered in new design)
    const table = metricsRef.current;
    if (!table) return;
    const ranks = ['A','K','Q','J','T','9','8','7','6','5','4','3','2'];
    // Equity vs random hand (MP2 range) — based on Power-Equilab data
    const eqGrid = [
      [+8.5,+6.5,+6.3,+5.9,+5.5,+4.5,+4.3,+3.9,+3.5,+3.3,+3.1,+2.9,+2.5], // A row
      [+5.3,+8.2,+5.1,+4.7,+4.3,+3.1,+2.8,+2.3,+1.9,+1.5,+1.2,+0.9,+0.5], // K row
      [+4.9,+4.3,+7.7,+4.1,+3.7,+2.2,+1.8,+1.3,+0.8,+0.3,-0.1,-0.4,-0.8], // Q row
      [+4.3,+3.8,+3.5,+7.2,+3.4,+1.7,+1.2,+0.6,+0.1,-0.4,-0.8,-1.2,-1.6], // J row
      [+3.8,+3.3,+3.0,+2.7,+6.7,+1.4,+0.8,+0.2,-0.4,-0.9,-1.4,-1.8,-2.2], // T row
      [+2.7,+1.8,+1.3,+0.9,+0.7,+5.9,+0.3,-0.3,-0.8,-1.3,-1.7,-2.1,-2.5], // 9 row
      [+2.3,+1.3,+0.7,+0.3,-0.1,-0.2,+5.0,-0.6,-1.1,-1.6,-2.0,-2.4,-2.8], // 8 row
      [+1.8,+0.8,+0.2,-0.3,-0.7,-0.9,-1.0,+4.1,-1.3,-1.8,-2.2,-2.6,-3.0], // 7 row
      [+1.3,+0.3,-0.3,-0.8,-1.2,-1.3,-1.4,-1.5,+3.2,-2.0,-2.4,-2.8,-3.2], // 6 row
      [+0.9,-0.1,-0.7,-1.2,-1.6,-1.7,-1.8,-1.9,-2.0,+2.3,-2.6,-3.0,-3.4], // 5 row
      [+0.5,-0.5,-1.1,-1.6,-2.0,-2.1,-2.2,-2.3,-2.4,-2.5,+1.5,-3.2,-3.6], // 4 row
      [+0.2,-0.8,-1.4,-1.9,-2.3,-2.4,-2.5,-2.6,-2.7,-2.8,-2.9,+0.8,-3.8], // 3 row
      [-0.1,-1.1,-1.8,-2.3,-2.7,-2.8,-2.9,-3.0,-3.1,-3.2,-3.3,-3.4,+0.2], // 2 row
    ];
    function getEV(i, j) { return eqGrid[i][j]; }
    function evToBg(ev) {
      if (ev >= 8) return 'rgba(74,222,128,0.85)';
      if (ev >= 5) return 'rgba(74,222,128,0.65)';
      if (ev >= 3) return 'rgba(74,222,128,0.45)';
      if (ev >= 1) return 'rgba(74,222,128,0.25)';
      if (ev >= 0) return 'rgba(74,222,128,0.1)';
      if (ev >= -1) return 'rgba(252,129,129,0.15)';
      if (ev >= -2) return 'rgba(252,129,129,0.3)';
      if (ev >= -3) return 'rgba(252,129,129,0.5)';
      return 'rgba(252,129,129,0.7)';
    }
    function evToColor(ev) { return ev > 0 ? '#4ADE80' : ev === 0 ? 'rgba(255,255,255,.4)' : '#FC8181'; }
    let thead = '<thead><tr><th style="border:none;background:transparent;"></th>';
    ranks.forEach(r => { thead += `<th style="border:none;background:transparent;color:rgba(255,255,255,.4);font-size:0.65rem;padding:4px 6px;text-align:center;font-weight:700;">${r}</th>`; });
    thead += '</tr></thead>';
    table.innerHTML = thead;
    const tbody = document.createElement('tbody');
    ranks.forEach((r1, i) => {
      const row = document.createElement('tr');
      const th = document.createElement('td');
      th.style.cssText = 'border:none;background:transparent;color:rgba(255,255,255,.4);font-weight:700;font-size:0.68rem;padding:0 8px;text-align:center;';
      th.textContent = r1;
      row.appendChild(th);
      ranks.forEach((r2, j) => {
        const td = document.createElement('td');
        const hand = i < j ? r1+r2+'s' : i === j ? r1+r2 : r1+r2+'o';
        const ev = getEV(i, j);
        td.style.cssText = `width:40px;height:32px;text-align:center;border:1px solid rgba(255,255,255,.04);border-radius:3px;cursor:pointer;vertical-align:middle;padding:1px 2px;background:${evToBg(ev)};transition:transform .15s;`;
        td.innerHTML = `<span style="font-size:0.5rem;color:rgba(255,255,255,.4);display:block;">${hand}</span><span style="font-size:0.58rem;font-weight:700;display:block;color:${evToColor(ev)}">${ev > 0 ? '+' : ''}${ev}</span>`;
        td.onmouseover = function() { this.style.transform = 'scale(1.18) translateY(-5px)'; this.style.zIndex = '20'; this.style.position = 'relative'; this.style.boxShadow = '0 8px 24px rgba(0,0,0,.6)'; };
        td.onmouseout = function() { this.style.transform = ''; this.style.zIndex = ''; this.style.boxShadow = ''; };
        row.appendChild(td);
      });
      tbody.appendChild(row);
    });
    table.appendChild(tbody);
  }, []);

  /* ── IntersectionObserver for fade-up animations ── */
  useEffect(() => {
    const els = document.querySelectorAll('.pm-fade');
    if (!els.length) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('pm-visible'); obs.unobserve(e.target); } });
    }, { threshold: 0.15 });
    els.forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const faqItems = [
    { q: 'Funciona com qual sala de poker?', a: 'GGPoker com suporte completo. PokerStars e 888Poker em breve.' },
    { q: 'Preciso saber matemática para usar?', a: 'Não. O modo Simples explica tudo sem fórmulas. O modo Técnico mostra os cálculos completos.' },
    { q: 'O plano grátis tem limite de tempo?', a: 'Não. Free para sempre. 3 análises e 5 perguntas/dia com o Coach.' },
    { q: 'Funciona para cash game também?', a: 'Sim. MTT, Spin&Go, Cash Game e SNG.' },
    { q: 'Posso cancelar quando quiser?', a: 'Sim. Sem fidelidade, sem multa.' },
    { q: 'O Coach IA substitui um coach humano?', a: 'O Coach IA é um complemento disponível 24h — ideal para estudo diário, análise de mãos e identificação de leaks. Para mentoria estratégica profunda, coaches humanos seguem sendo insubstituíveis. A PokerMind potencializa o que você já estuda.' },
  ];

  const features = [
    { tag: 'Coach IA', title: 'Coach IA 24/7', desc: 'Analise qualquer spot em segundos. Resposta técnica 24h, em português, adaptada ao seu nível.' },
    { tag: 'Analyzer', title: 'Análise de Mãos', desc: 'Saiba exatamente quanto cada erro custou em big blinds, sessão por sessão.' },
    { tag: 'Plano', title: 'Plano Personalizado', desc: 'Nada de estudar aleatório. Um plano feito para os seus leaks e atualizado conforme você evolui.' },
    { tag: 'Neural Engine', title: 'Neural Engine', desc: 'Visualize o EV de cada mão contra qualquer perfil de vilão. Milhares de combinações calculadas, filtradas por posição e estratégia de exploit.' },
    { tag: 'Dashboard', title: 'Dashboard de Evolução', desc: 'Veja sua evolução em números. Leak resolvido é dinheiro recuperado, rastreado sessão por sessão.' },
    { tag: 'Memória', title: 'Memória Inteligente', desc: 'O Coach lembra tudo. Cada sessão começa de onde a última parou.' },
  ];

  const testimonials = [
    { text: 'Identifiquei em 5 minutos um leak que me custava 3BB/100 há meses. Valeu cada centavo.', name: 'Rafael M.', role: 'Regular NL100 · Plano Pro' },
    { text: 'Finalmente um coach que fala português e entende o contexto do poker brasileiro de verdade.', name: 'Lucas S.', role: 'Grinder MTT · Plano Pro' },
    { text: 'Meu ROI subiu 8% em 30 dias só ajustando os leaks que o Coach identificou. Impressionante.', name: 'Carla T.', role: 'Jogadora de Live · Plano Elite' },
    { text: 'Estudei 2 anos em escola de poker e aprendi muito sobre teoria. Mas só quando comecei a analisar minhas mãos na PokerMind entendi onde eu errava na prática. São coisas diferentes — e as duas me fizeram evoluir.', name: 'Marcos V.', role: 'MTT Regular · Plano Pro' },
  ];

  const compRows = [
    ['Idioma', 'Inglês', 'Português'],
    ['Coach IA conversacional', '✗', '✓'],
    ['Plano de estudo personalizado', '✗', '✓'],
    ['Análise de mãos + EV', '✓', '✓'],
    ['Modo Exploit por perfil vilão', '✗', '✓'],
    ['Preço mensal', '~R$200–400', 'A partir de R$49/mês'],
    ['Teste grátis sem cartão', '✗', '✓'],
  ];

  /* ═══ RENDER ═══ */
  return (
    <div style={{ background: '#000', minHeight: '100vh', ...F }}>
      <style>{`
        @keyframes fadeUp { from { opacity: 0; transform: translateY(28px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes rowIn { from { opacity: 0; transform: translateX(-12px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: .4; } }
        .pm-fade { opacity: 0; transform: translateY(28px); transition: opacity 0.6s ease, transform 0.6s ease; }
        .pm-visible { opacity: 1; transform: translateY(0); }
        .pm-row { opacity: 0; animation: rowIn 0.5s ease forwards; }
        .pm-grad-border { position: relative; background: transparent; border: none; }
        .pm-grad-border::before { content: ''; position: absolute; inset: 0; border-radius: 7px; padding: 1.5px; background: ${GRAD}; -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0); -webkit-mask-composite: xor; mask-composite: exclude; pointer-events: none; }
        .pm-glow { transition: box-shadow 0.3s ease, transform 0.2s ease; }
        .pm-glow:hover { box-shadow: 0 0 24px rgba(0,230,51,0.35), 0 0 48px rgba(0,155,255,0.15); transform: translateY(-1px); }
        .pm-glow-blue { transition: box-shadow 0.3s ease, transform 0.2s ease; }
        .pm-glow-blue:hover { box-shadow: 0 0 24px rgba(0,155,255,0.4), 0 0 48px rgba(0,82,204,0.2); transform: translateY(-1px); }
        @keyframes glowBreath { 0%,100%{opacity:0.8;transform:scale(1)} 50%{opacity:1;transform:scale(1.05)} }
        @keyframes chipFloat { 0%,100%{transform:translateY(0) rotate(-3deg)} 50%{transform:translateY(-6px) rotate(3deg)} }
        @keyframes floatLeft { 0%,100%{transform:translateY(-50%) rotate(-12deg)} 50%{transform:translateY(calc(-50% - 12px)) rotate(-14deg)} }
        @keyframes floatRight { 0%,100%{transform:translateY(-50%) rotate(12deg)} 50%{transform:translateY(calc(-50% - 12px)) rotate(14deg)} }
        .neural-section { position:relative; overflow:hidden; }
        .neural-section::before { content:''; position:absolute; inset:0; z-index:0; pointer-events:none; background:radial-gradient(ellipse 80% 70% at 50% 10%, rgba(200,255,0,0.06) 0%, rgba(0,155,255,0.03) 45%, transparent 72%); }
        .neural-section::after { content:''; position:absolute; bottom:0; left:0; right:0; height:180px; z-index:0; pointer-events:none; background:linear-gradient(180deg, transparent 0%, rgba(0,30,80,0.03) 50%, rgba(0,30,80,0.06) 100%); }
        .neural-section > * { position:relative; z-index:1; }
      `}</style>

      {/* ══════════════════════════════════════
          NAVBAR
         ══════════════════════════════════════ */}
      <nav style={{
        position: 'sticky', top: 0, zIndex: 100,
        background: 'rgba(10,10,10,0.93)', backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)',
        borderBottom: '1px solid rgba(255,255,255,0.05)',
        height: 64, padding: '0 48px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        {/* Logo */}
        <Link to="/" onClick={handleLogoClick} style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
          <img src={logoImg} alt="PokerMind Brasil" style={{ height: 45, width: 203, objectFit: 'contain' }} />
        </Link>

        {/* Center links */}
        <div style={{ display: 'flex', gap: 32 }}>
          {['Recursos', 'Neural Engine', 'Planos', 'FAQ'].map(l => {
            const href = l === 'Neural Engine' ? '#neural-engine' : `#${l.toLowerCase().replace(/ /g, '-')}`;
            return (
              <a key={l} href={href} style={{ color: 'rgba(255,255,255,0.4)', textDecoration: 'none', fontSize: 14, fontWeight: 500, transition: 'color 0.2s', ...F }} onMouseEnter={e => e.currentTarget.style.color = '#fff'} onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.4)'}>{l}</a>
            );
          })}
        </div>

        {/* Right */}
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {isLogado ? (
            <Link to="/dashboard" className="pm-glow" style={{ background: GRAD_CTA, color: '#000', padding: '10px 22px', borderRadius: 8, fontWeight: 800, fontSize: 14, textDecoration: 'none', ...F }}>Área do Membro →</Link>
          ) : (
            <>
              <Link to="/login" style={{ border: '1px solid rgba(255,255,255,0.15)', background: 'transparent', color: '#fff', padding: '9px 20px', borderRadius: 8, fontWeight: 500, fontSize: 14, textDecoration: 'none', ...F }}>Entrar</Link>
              <Link to="/cadastro" className="pm-glow" style={{ background: GRAD_CTA, color: '#000', padding: '10px 22px', borderRadius: 8, fontWeight: 800, fontSize: 14, textDecoration: 'none', ...F }}>Começar grátis</Link>
            </>
          )}
        </div>
      </nav>

      {/* ══════════════════════════════════════
          HERO
         ══════════════════════════════════════ */}
      <section style={{ position: 'relative', minHeight: 620, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '80px 40px 60px', overflow: 'hidden' }}>
        {/* Video */}
        <video ref={videoRef} id="hero-video" autoPlay muted loop playsInline style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 0, opacity: 0.25 }} />

        {/* Overlay dark */}
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.5) 50%, rgba(0,0,0,0.8) 100%)', zIndex: 1 }} />

        {/* Neon line at top */}
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 1, background: 'linear-gradient(90deg, transparent 0%, #00E633 30%, #009BFF 70%, transparent 100%)', zIndex: 3 }} />

        {/* Content */}
        <div style={{ position: 'relative', zIndex: 2, maxWidth: 700 }}>
          {/* Label */}
          <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', marginBottom: 20, ...F }}>O primeiro Coach IA de poker do Brasil</div>

          <h1 style={{ fontSize: 80, fontWeight: 900, textTransform: 'uppercase', lineHeight: 0.9, letterSpacing: -2, margin: 0, ...F }}>
            <span style={{ color: '#fff' }}>PARE DE ADIVINHAR.</span><br /><span style={gradText}>DOMINE CADA DECISÃO.</span>
          </h1>

          <p style={{ fontSize: 16, fontWeight: 400, color: 'rgba(255,255,255,0.5)', lineHeight: 1.6, margin: '20px auto 28px', maxWidth: 520, ...F }}>
            Descubra exatamente onde você perde fichas. Corrija com método.
          </p>

          <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/cadastro" className="pm-glow" style={{ background: 'linear-gradient(135deg,#00E633,#009BFF)', color: '#000', padding: '16px 34px', borderRadius: 8, fontWeight: 800, fontSize: 16, textTransform: 'uppercase', textDecoration: 'none', ...F }}>
              COMEÇAR GRÁTIS, SEM CARTÃO
            </Link>
            <a href="#recursos" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.7)', background: 'transparent', padding: '13px 28px', borderRadius: 8, fontWeight: 600, fontSize: 14, textTransform: 'uppercase', letterSpacing: 0.5, textDecoration: 'none', transition: 'all 0.2s', ...F }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.4)'; e.currentTarget.style.color = 'rgba(255,255,255,0.9)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)'; e.currentTarget.style.color = 'rgba(255,255,255,0.7)'; }}>
              ▶ Ver funcionalidades
            </a>
          </div>

          <p style={{ marginTop: 32, fontSize: 13, fontWeight: 400, color: 'rgba(255,255,255,0.4)', ...F }}>
            <span style={{ color: '#C8FF00', letterSpacing: 2 }}>★★★★★</span>&nbsp;&nbsp;Rafael M., Regular NL100
          </p>
        </div>
      </section>

      {/* ══════════════════════════════════════
          METRICS STRIP
         ══════════════════════════════════════ */}
      <div className="pm-fade" style={{ background: '#000', borderTop: '1px solid rgba(255,255,255,0.05)', borderBottom: '1px solid rgba(255,255,255,0.05)', padding: '48px 40px', display: 'flex', justifyContent: 'center', gap: 80, flexWrap: 'wrap', position: 'relative', overflow: 'hidden' }}>
        {[
          { num: '+R$4.280', label: 'EV médio identificado por usuário' },
          { num: '1.2K', label: 'Mãos analisadas por sessão' },
          { num: '78.5%', label: 'Precisão média nos drills' },
          { num: '2K+', label: 'Jogadores ativos na plataforma' },
        ].map(m => (
          <div key={m.label} style={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
            <div style={{ ...gradText, fontSize: 52, fontWeight: 900, ...F }}>{m.num}</div>
            <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 13, fontWeight: 500, marginTop: 4, ...F }}>{m.label}</div>
          </div>
        ))}
      </div>

      {/* ══════════════════════════════════════
          FUNCIONALIDADES
         ══════════════════════════════════════ */}
      <section id="recursos" style={{ background: '#0a0a0a', padding: '72px 48px' }}>
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <h2 className="pm-fade" style={{ fontSize: 48, fontWeight: 900, textTransform: 'uppercase', letterSpacing: -1, margin: 0, ...F }}>
            <span style={{ color: '#fff' }}>TUDO QUE VOCÊ PRECISA </span><span style={gradText}>PARA EVOLUIR.</span>
          </h2>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, maxWidth: 860, margin: '0 auto' }}>
          {features.map((f, i) => (
            <div key={f.tag} className="pm-fade" style={{ background: '#111', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12, padding: 28, transition: 'border-color 0.2s', animationDelay: `${i * 0.08}s` }}
              onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(0,230,51,0.25)'}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)'}>
              <span style={{ display: 'inline-block', background: GRAD, color: '#000', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 2, padding: '3px 10px', borderRadius: 4, marginBottom: 14, ...F }}>{f.tag}</span>
              <h3 style={{ color: '#fff', fontSize: 22, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.3, margin: '0 0 10px', ...F }}>{f.title}</h3>
              <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 14, fontWeight: 400, lineHeight: 1.6, margin: 0, ...F }}>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ══════════════════════════════════════
          NEURAL ENGINE (EV table)
         ══════════════════════════════════════ */}
      <section id="neural-engine" className="neural-section" style={{ background: '#0a0a0a', padding: '64px 48px', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'relative', zIndex: 1, textAlign: 'center', marginBottom: 32 }}>
          <h2 className="pm-fade" style={{ fontSize: 44, fontWeight: 900, textTransform: 'uppercase', letterSpacing: -1, margin: 0, ...F }}>
            <span style={{ color: '#fff' }}>POKERMIND </span><span style={{ color: '#C8FF00' }}>NEURAL ENGINE</span>
          </h2>
          <p className="pm-fade" style={{ color: 'rgba(255,255,255,0.4)', fontSize: 15, maxWidth: 520, margin: '12px auto 0', lineHeight: 1.7, ...F }}>
            Visualize o EV de cada mão contra qualquer perfil de vilão. Milhares de combinações calculadas, filtradas por posição, métrica e estratégia de exploit.
          </p>
        </div>

        <div className="pm-fade" style={{ maxWidth: 820, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          {/* Villain filters */}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
            {['Nit','Tight Passivo','TAG','PokerMinder','Iniciante','LAG','Calling Station'].map(p => {
              const isActive = p === 'PokerMinder';
              const isGreen = p === 'Iniciante';
              const isRed = p === 'Calling Station';
              return (
                <div key={p} style={{
                  padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  background: isActive ? 'rgba(200,255,0,0.12)' : isGreen ? 'rgba(0,230,51,0.08)' : isRed ? 'rgba(229,57,53,0.08)' : 'rgba(255,255,255,0.04)',
                  border: `1px solid ${isActive ? 'rgba(200,255,0,0.35)' : isGreen ? 'rgba(0,230,51,0.2)' : isRed ? 'rgba(229,57,53,0.2)' : 'rgba(255,255,255,0.08)'}`,
                  color: isActive ? '#C8FF00' : isGreen ? '#00E633' : isRed ? '#e53935' : 'rgba(255,255,255,0.5)',
                  ...F,
                }}>{p}</div>
              );
            })}
          </div>

          {/* Metric tabs */}
          <div style={{ display: 'flex', gap: 4, justifyContent: 'center', marginBottom: 12 }}>
            {['Equity %','Win %','EV (BB)','EV Relativo','a_BE','FE% p/ Shove'].map(t => {
              const isActive = t === 'EV (BB)';
              return (
                <div key={t} style={{
                  padding: '6px 14px', borderRadius: 5, fontSize: 12, fontWeight: isActive ? 700 : 500, cursor: 'pointer',
                  background: isActive ? 'rgba(200,255,0,0.1)' : 'transparent',
                  color: isActive ? '#C8FF00' : 'rgba(255,255,255,0.35)',
                  borderBottom: isActive ? '2px solid #C8FF00' : '2px solid transparent',
                  ...F,
                }}>{t}</div>
              );
            })}
          </div>

          {/* Info bar */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8, marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
            <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', ...F }}>Range: <strong style={{ color: '#fff' }}>22+, A2s+, A8o+</strong></span>
            <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', ...F }}>EV (BB) médio: <strong style={{ color: '#C8FF00' }}>1.3bb</strong></span>
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', ...F }}>Valor esperado em big blinds ao chamar bet de 75%</span>
          </div>

          {/* Table */}
          <div style={{ overflowX: 'auto', background: '#111', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12, padding: 12 }}>
            <table ref={metricsRef} style={{ borderCollapse: 'separate', borderSpacing: 2, width: '100%', ...F }} />
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          POR QUE O POKERMIND
         ══════════════════════════════════════ */}
      <section id="como-funciona" style={{ background: '#0d0d0d', borderTop: '1px solid rgba(255,255,255,0.05)', borderBottom: '1px solid rgba(255,255,255,0.05)', padding: '72px 48px', position: 'relative', overflow: 'hidden' }}>

        <div style={{ position: 'relative', maxWidth: 1100, margin: '0 auto', textAlign: 'center' }}>
          <div className="pm-fade" style={{ ...gradText, fontSize: 13, fontWeight: 700, letterSpacing: 3, marginBottom: 16, ...F }}>Por que o PokerMind Brasil?</div>

          <h2 className="pm-fade" style={{ fontSize: 48, fontWeight: 900, textTransform: 'uppercase', letterSpacing: -1, lineHeight: 0.95, margin: '0 0 0', ...F }}>
            <span style={{ color: '#fff' }}>A mesma </span><em style={{ ...gradText, fontStyle: 'italic' }}>qualidade.</em><br />
            <span style={{ color: '#fff' }}>Em </span><em style={{ color: '#fff', fontStyle: 'italic' }}>português.</em><br />
            <em style={{ ...gradText, fontStyle: 'italic' }}>Pelo preço certo.</em>
          </h2>

          {/* Subtitle */}
          <p className="pm-fade" style={{ fontSize: 16, fontWeight: 400, color: 'rgba(255,255,255,0.45)', maxWidth: 600, margin: '16px auto 56px', lineHeight: 1.7, textAlign: 'center', fontStyle: 'italic', ...F }}>
            Não é um curso solto. Não é só uma ferramenta. É um sistema completo, do conceito básico à análise de leak, com trilhas estruturadas e um Coach IA que aprende com você.
          </p>

          {/* Comparison tables side by side */}
          <div className="pm-fade" style={{ display: 'flex', gap: '16px', alignItems: 'stretch' }}>
            {/* Table 1 */}
            <div style={{ flex: '1 1 0', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 2, color: 'rgba(255,255,255,0.3)', marginBottom: 10, textAlign: 'center', ...F }}>vs. Ferramentas Internacionais</div>
              <div style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.05)', flex: 1 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', ...F }}>
                  <thead>
                    <tr style={{ background: '#141414' }}>
                      <th style={{ width: '40%', padding: '8px 12px', border: 'none' }}></th>
                      <th style={{ padding: '8px 12px', textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: 11, fontWeight: 500, borderBottom: '1px solid rgba(255,255,255,0.06)', border: 'none', ...F }}>Internacionais</th>
                      <th style={{ padding: '8px 12px', textAlign: 'center', borderBottom: '2px solid #00E633', border: 'none' }}><span style={{ color: '#00E633', fontWeight: 700, fontSize: 11, ...F }}>PokerMind</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    {compRows.map(([feat, other, pm], i) => (
                      <tr key={feat} className="pm-row" style={{ animationDelay: `${i * 0.08}s`, background: i % 2 === 0 ? '#0f0f0f' : '#0a0a0a' }}>
                        <td style={{ padding: '7px 12px', fontSize: 13, color: 'rgba(255,255,255,0.6)', fontWeight: 500, borderBottom: '1px solid rgba(255,255,255,0.04)', ...F }}>{feat}</td>
                        <td style={{ padding: '7px 12px', textAlign: 'center', fontSize: 13, borderBottom: '1px solid rgba(255,255,255,0.04)', color: other === '✗' ? 'rgba(255,255,255,0.2)' : other === '✓' ? '#00E633' : 'rgba(255,255,255,0.25)', fontWeight: other === '✓' ? 700 : 400, ...F }}>{other}</td>
                        <td style={{ padding: '7px 12px', textAlign: 'center', fontSize: pm === '✓' ? 16 : 13, borderBottom: '1px solid rgba(255,255,255,0.04)', ...F }}><span style={{ color: '#00E633', fontWeight: 700 }}>{pm}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Table 2 */}
            <div style={{ flex: '1 1 0', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 2, color: 'rgba(255,255,255,0.3)', marginBottom: 10, textAlign: 'center', ...F }}>vs. Escolas e Cursos</div>
              <div style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.05)', flex: 1 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', ...F }}>
                  <thead>
                    <tr style={{ background: '#141414' }}>
                      <th style={{ width: '40%', padding: '8px 12px', border: 'none' }}></th>
                      <th style={{ padding: '8px 12px', textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: 11, fontWeight: 500, borderBottom: '1px solid rgba(255,255,255,0.06)', border: 'none', ...F }}>Escolas/cursos</th>
                      <th style={{ padding: '8px 12px', textAlign: 'center', borderBottom: '2px solid #00E633', border: 'none' }}><span style={{ color: '#00E633', fontWeight: 700, fontSize: 11, ...F }}>PokerMind</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ['Trilha de estudo', 'Você monta a sua', 'Gerada pelo Coach IA'],
                      ['Conteúdo', 'Aulas gravadas', 'Análise + aulas integradas'],
                      ['Personalização', '✗', '✓'],
                      ['Feedback', 'Você descobre sozinho', 'Imediata após cada sessão'],
                      ['Disponibilidade', 'Horário do coach', '24h, qualquer dúvida'],
                      ['Progressão', 'Você decide o ritmo', 'Adaptativa por gap'],
                      ['Preço', 'R$500–2.000/curso', 'A partir de R$49/mês'],
                    ].map(([feat, other, pm], i) => (
                      <tr key={feat} className="pm-row" style={{ animationDelay: `${(i + 1) * 0.08}s`, background: i % 2 === 0 ? '#0f0f0f' : '#0a0a0a' }}>
                        <td style={{ padding: '7px 12px', fontSize: 13, color: 'rgba(255,255,255,0.6)', fontWeight: 500, borderBottom: '1px solid rgba(255,255,255,0.04)', ...F }}>{feat}</td>
                        <td style={{ padding: '7px 12px', textAlign: 'center', fontSize: 13, borderBottom: '1px solid rgba(255,255,255,0.04)', color: other === '✗' ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.25)', fontWeight: 400, ...F }}>{other}</td>
                        <td style={{ padding: '7px 12px', textAlign: 'center', fontSize: pm === '✓' ? 16 : 13, borderBottom: '1px solid rgba(255,255,255,0.04)', ...F }}><span style={{ color: '#00E633', fontWeight: 700 }}>{pm}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* ── Text below table 2 ── */}
          <div className="pm-fade" style={{ textAlign: 'center', margin: '32px auto 0', maxWidth: 480 }}>
            <p style={{ fontSize: 15, fontWeight: 500, color: 'rgba(255,255,255,0.4)', fontStyle: 'italic', margin: 0, ...F }}>Cursos ensinam o jogo.</p>
            <p style={{ fontSize: 17, fontWeight: 700, color: '#ffffff', fontStyle: 'normal', margin: '6px 0 0', lineHeight: 1.5, ...F }}>A PokerMind descobre o que você ainda erra e te guia para corrigir com método.</p>
          </div>

          {/* ── CTA button ── */}
          <div className="pm-fade" style={{ marginTop: 32 }}>
            <Link to="/cadastro" className="pm-glow" style={{ display: 'inline-block', background: GRAD_CTA, color: '#000', padding: '16px 36px', borderRadius: 8, fontWeight: 800, fontSize: 15, textTransform: 'uppercase', letterSpacing: 0.5, textDecoration: 'none', ...F }}>Começar grátis, sem cartão</Link>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          PROVA SOCIAL
         ══════════════════════════════════════ */}
      <section style={{ background: '#060606', borderTop: '1px solid rgba(255,255,255,0.05)', borderBottom: '1px solid rgba(255,255,255,0.05)', padding: '72px 48px' }}>
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <div className="pm-fade" style={{ ...gradText, fontSize: 13, fontWeight: 700, letterSpacing: 3, marginBottom: 12, ...F }}>Prova Social</div>
          <h2 className="pm-fade" style={{ fontSize: 36, fontWeight: 900, textTransform: 'uppercase', letterSpacing: -1, margin: 0, ...F }}>
            <span style={{ color: '#fff' }}>O QUE OS </span><span style={gradText}>JOGADORES</span><span style={{ color: '#fff' }}> DIZEM</span>
          </h2>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, maxWidth: 1100, margin: '0 auto' }}>
          {testimonials.map((t, i) => (
            <div key={t.name} className="pm-fade" style={{ background: '#111', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 12, padding: '22px 24px', transition: 'border-color 0.2s', animationDelay: `${(i + 1) * 0.1}s` }}
              onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(0,230,51,0.2)'}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.05)'}>
              <div style={{ color: '#C8FF00', letterSpacing: 3, marginBottom: 10, fontSize: 13, ...F }}>★★★★★</div>
              <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13, fontStyle: 'italic', lineHeight: 1.6, margin: '0 0 14px', fontWeight: 400, ...F }}>"{t.text}"</p>
              <div style={{ fontSize: 14, fontWeight: 800, color: '#fff', textTransform: 'uppercase', letterSpacing: 0.5, ...F }}>{t.name}</div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.28)', fontWeight: 500, marginTop: 2, ...F }}>{t.role}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ══════════════════════════════════════
          PLANOS
         ══════════════════════════════════════ */}
      <section id="planos" style={{ background: '#0d0d0d', borderTop: '1px solid rgba(255,255,255,0.05)', padding: '72px 48px', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'relative', zIndex: 1, textAlign: 'center', marginBottom: 8 }}>
          <div className="pm-fade" style={{ ...gradText, fontSize: 13, fontWeight: 700, letterSpacing: 3, marginBottom: 12, ...F }}>Planos</div>
          <h2 className="pm-fade" style={{ fontSize: 56, fontWeight: 900, textTransform: 'uppercase', letterSpacing: -1, margin: 0, ...F }}>
            <span style={{ color: '#fff' }}>COMECE GRÁTIS. </span><span style={gradText}>EVOLUA COM MÉTODO.</span>
          </h2>
          <p className="pm-fade" style={{ color: 'rgba(255,255,255,0.4)', fontSize: 15, marginTop: 12, ...F }}>Sem fidelidade. Cancele quando quiser.</p>
        </div>

        {/* Billing toggle */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14, margin: '36px 0 40px', position: 'relative', zIndex: 1 }}>
          <span style={{ fontSize: 14, fontWeight: 500, color: isAnual ? 'rgba(255,255,255,0.4)' : '#fff', ...F }}>Mensal</span>
          <div onClick={toggleBilling} style={{ width: 52, height: 28, borderRadius: 14, background: GRAD_CTA, cursor: 'pointer', position: 'relative', flexShrink: 0 }}>
            <div style={{ width: 22, height: 22, borderRadius: '50%', background: '#000', position: 'absolute', top: 3, left: 3, transition: 'transform 0.25s', transform: isAnual ? 'translateX(24px)' : 'translateX(0)' }} />
          </div>
          <span style={{ fontSize: 14, fontWeight: 500, color: isAnual ? '#fff' : 'rgba(255,255,255,0.4)', ...F }}>
            Anual <span style={{ background: 'rgba(0,230,51,0.12)', color: '#00E633', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 4, marginLeft: 6 }}>-20%</span>
          </span>
        </div>

        {/* Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, maxWidth: 920, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          {/* FREE */}
          <div className="pm-fade" style={{ background: '#111', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 14, padding: 32 }}>
            <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, ...F }}>FREE</div>
            <div style={{ fontSize: 44, fontWeight: 900, color: '#fff', margin: '8px 0 4px', ...F }}>R$0</div>
            <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 13, marginBottom: 20, ...F }}>para sempre</div>
            <div style={{ height: 1, background: 'rgba(255,255,255,0.07)', marginBottom: 20 }} />
            {[
              { text: '3 análises de mãos por mês' },
              { text: '5 perguntas/dia ao Oráculo', sub: '(Coach IA da PokerMind)' },
              { text: 'Dashboard básico' },
              { text: 'Onboarding personalizado' },
            ].map(f => (
              <div key={f.text} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 10 }}>
                <span style={{ color: '#00E633', fontWeight: 700, fontSize: 14 }}>✓</span>
                <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 14, ...F }}>{f.text}{f.sub && <span style={{ display: 'block', fontSize: 11, color: 'rgba(255,255,255,0.35)', fontStyle: 'italic', marginTop: 2, ...F }}>{f.sub}</span>}</span>
              </div>
            ))}
            <Link to="/cadastro" style={{ display: 'block', marginTop: 24, border: '1px solid rgba(255,255,255,0.15)', background: 'transparent', color: '#fff', textAlign: 'center', padding: '12px 0', borderRadius: 8, fontWeight: 700, fontSize: 14, textTransform: 'uppercase', textDecoration: 'none', ...F }}>CRIAR CONTA GRÁTIS</Link>
          </div>

          {/* PRO — Green identity */}
          <div className="pm-fade" style={{ backgroundImage: `linear-gradient(#111,#111), ${GRAD_PRO}`, backgroundOrigin: 'border-box', backgroundClip: 'padding-box, border-box', border: '2px solid transparent', borderRadius: 14, padding: 32, position: 'relative' }}>
            <div style={{ position: 'absolute', top: -13, left: '50%', transform: 'translateX(-50%)', background: GRAD_PRO, color: '#000', fontSize: 9, fontWeight: 800, padding: '3px 12px', borderRadius: 12, whiteSpace: 'nowrap', letterSpacing: 1, ...F }}>★ MAIS POPULAR</div>
            <div style={{ ...proText, fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, ...F }}>PRO</div>
            <div style={{ ...proText, fontSize: 44, fontWeight: 900, margin: '8px 0 4px', ...F }}>R${proPrice}</div>
            <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 13, marginBottom: 20, ...F }}>{proPeriod} · 7 dias grátis</div>
            <div style={{ height: 1, background: 'rgba(255,255,255,0.07)', marginBottom: 20 }} />
            {[
              { text: 'Oráculo 24h, perguntas ilimitadas', sub: '(Coach IA da PokerMind)' },
              { text: 'Até 20 sessões analisadas por mês' },
              { text: 'Trilha de evolução gerada no seu perfil' },
              { text: 'Estratégia específica para 7 perfis de adversário' },
              { text: 'Coach que lembra seus leaks e evolução' },
              { text: 'Dashboard completo com ROI e evolução por sessão' },
            ].map(f => (
              <div key={f.text} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 10 }}>
                <span style={{ color: '#00E633', fontWeight: 700, fontSize: 14 }}>✓</span>
                <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 14, ...F }}>{f.text}{f.sub && <span style={{ display: 'block', fontSize: 11, color: 'rgba(255,255,255,0.35)', fontStyle: 'italic', marginTop: 2, ...F }}>{f.sub}</span>}</span>
              </div>
            ))}
            {/* Ancoragem Pro */}
            <div style={{ borderTop: '1px solid rgba(0,230,51,0.1)', paddingTop: 16, marginTop: 4, marginBottom: 16 }}>
              <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)', textAlign: 'center', lineHeight: 1.5, fontStyle: 'italic', margin: 0, ...F }}>Usuários Pro identificam em média <span style={{ color: '#00E633', fontWeight: 700, fontStyle: 'normal' }}>R$4.280</span> em EV perdido nas primeiras semanas.</p>
            </div>
            <a href={isAnual ? 'https://pay.kiwify.com.br/qK5PmRb' : 'https://pay.kiwify.com.br/BAEGpJr'} target="_blank" rel="noopener noreferrer" className="pm-glow" style={{ display: 'block', background: GRAD_PRO, color: '#000', textAlign: 'center', padding: '13px 0', borderRadius: 8, fontWeight: 800, fontSize: 14, textTransform: 'uppercase', textDecoration: 'none', ...F }}>COMEÇAR PRO, 7 DIAS GRÁTIS</a>
          </div>

          {/* ELITE — Blue identity */}
          <div className="pm-fade"
            style={{ background: 'rgba(0,30,80,0.12)', backgroundImage: `linear-gradient(rgba(0,30,80,0.12),rgba(0,30,80,0.12)), linear-gradient(#111,#111), ${GRAD_ELITE}`, backgroundOrigin: 'border-box', backgroundClip: 'padding-box, padding-box, border-box', border: '2px solid transparent', borderRadius: 14, padding: 32, position: 'relative' }}>
            <div style={{ position: 'absolute', top: -13, left: '50%', transform: 'translateX(-50%)', background: GRAD_ELITE, color: '#fff', fontSize: 9, fontWeight: 800, padding: '3px 12px', borderRadius: 20, whiteSpace: 'nowrap', letterSpacing: 1.5, textTransform: 'uppercase', ...F }}>★ O MAIS COMPLETO</div>
            <div style={{ ...eliteText, fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, ...F }}>ELITE</div>
            {/* Price + badge inline */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, margin: '8px 0 4px' }}>
              <div style={{ ...eliteText, fontSize: 44, fontWeight: 900, ...F }}>R${elitePrice}</div>
              {/* Poker chip badge */}
              <div style={{ position: 'relative', width: 64, height: 64, flexShrink: 0 }}>
                <div style={{ position: 'absolute', inset: -10, borderRadius: '50%', background: 'radial-gradient(circle, rgba(0,155,255,0.5) 0%, rgba(0,82,204,0.2) 40%, transparent 70%)', animation: 'glowBreath 3s ease-in-out infinite', pointerEvents: 'none' }} />
                <svg viewBox="0 0 220 220" width="64" height="64" style={{ position: 'relative', zIndex: 1, animation: 'chipFloat 3s ease-in-out infinite' }}>
                  <defs>
                    <filter id="elGlow"><feGaussianBlur stdDeviation="2.5" /><feComposite in="SourceGraphic" /></filter>
                    <filter id="crGlow"><feGaussianBlur stdDeviation="3.5" result="g1" /><feGaussianBlur in="SourceGraphic" stdDeviation="7" result="g2" /><feMerge><feMergeNode in="g1" /><feMergeNode in="g2" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
                    <radialGradient id="elFace" cx="50%" cy="42%" r="55%"><stop offset="0%" stopColor="#1245a8" /><stop offset="60%" stopColor="#081e55" /><stop offset="100%" stopColor="#020918" /></radialGradient>
                  </defs>
                  <circle cx="110" cy="110" r="105" fill="#061535" stroke="#009BFF" strokeWidth="3" filter="url(#elGlow)" />
                  <circle cx="110" cy="110" r="92" fill="none" stroke="#0b2368" strokeWidth="12" />
                  <circle cx="110" cy="110" r="74" fill="#030b1e" stroke="#009BFF" strokeWidth="2.5" filter="url(#elGlow)" />
                  <circle cx="110" cy="110" r="68" fill="url(#elFace)" />
                  <text x="110" y="22" textAnchor="middle" fill="#009BFF" fontSize="26" fontFamily="serif">♠</text>
                  <text x="198" y="118" textAnchor="middle" fill="#009BFF" fontSize="26" fontFamily="serif">♥</text>
                  <text x="110" y="206" textAnchor="middle" fill="#009BFF" fontSize="26" fontFamily="serif">♦</text>
                  <text x="22" y="118" textAnchor="middle" fill="#009BFF" fontSize="26" fontFamily="serif">♣</text>
                  <text x="172" y="55" textAnchor="middle" fill="#009BFF" fontSize="20" opacity="0.78" fontFamily="serif">♣</text>
                  <text x="48" y="55" textAnchor="middle" fill="#009BFF" fontSize="20" opacity="0.78" fontFamily="serif">♦</text>
                  <text x="172" y="180" textAnchor="middle" fill="#009BFF" fontSize="20" opacity="0.78" fontFamily="serif">♠</text>
                  <text x="48" y="180" textAnchor="middle" fill="#009BFF" fontSize="20" opacity="0.78" fontFamily="serif">♥</text>
                  <g transform="translate(110,110)" filter="url(#crGlow)"><rect x="-36" y="20" width="72" height="10" rx="3" fill="white" /><path d="M-36,21 L-36,-7 L-18,17 L0,-28 L18,17 L36,-7 L36,21 Z" fill="white" /><circle cx="-36" cy="-7" r="5.5" fill="white" /><circle cx="0" cy="-28" r="5.5" fill="white" /><circle cx="36" cy="-7" r="5.5" fill="white" /></g>
                </svg>
              </div>
            </div>
            <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 13, ...F }}>{elitePeriod}</div>
            <div style={{ color: 'rgba(0,155,255,0.5)', fontSize: 12, fontWeight: 500, fontStyle: 'italic', marginTop: 4, marginBottom: 20, ...F }}>Para quem leva o poker a sério</div>
            <div style={{ height: 1, background: 'rgba(255,255,255,0.07)', marginBottom: 20 }} />
            {['Tudo do Pro, sem exceção', 'Histórico completo, sem limite de upload de mãos', 'Análise processada em prioridade máxima', 'Relatório mensal de evolução gerado pelo Coach', 'Acesso beta a novas features antes do lançamento', 'Badge Elite exclusivo no perfil da comunidade', 'Suporte prioritário com resposta em até 24 horas'].map(f => (
              <div key={f} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 10 }}>
                <span style={{ color: '#009BFF', fontWeight: 700, fontSize: 14 }}>✓</span>
                <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 14, ...F }}>{f}</span>
              </div>
            ))}
            {/* Ancoragem Elite */}
            <div style={{ borderTop: '1px solid rgba(0,155,255,0.12)', paddingTop: 16, marginTop: 4, marginBottom: 16 }}>
              <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)', textAlign: 'center', lineHeight: 1.6, fontStyle: 'italic', margin: 0, ...F }}>Coach humano cobra R$300/hora.<br /><span style={{ fontStyle: 'normal' }}>O Elite custa <span style={{ ...eliteText, fontWeight: 700 }}>R$97/mês</span>.</span></p>
            </div>
            <a href={isAnual ? 'https://pay.kiwify.com.br/OPgs2ZL' : 'https://pay.kiwify.com.br/AV0kIMK'} target="_blank" rel="noopener noreferrer" className="pm-glow-blue" style={{ display: 'block', background: GRAD_ELITE, color: '#fff', textAlign: 'center', padding: '13px 0', borderRadius: 8, fontWeight: 800, fontSize: 14, textTransform: 'uppercase', textDecoration: 'none', width: '100%', transition: 'opacity 0.2s', ...F }}>ENTRAR PRA ELITE →</a>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          FAQ
         ══════════════════════════════════════ */}
      <section id="faq" style={{ background: '#0a0a0a', borderTop: '1px solid rgba(255,255,255,0.04)', padding: '72px 48px' }}>
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <div className="pm-fade" style={{ ...gradText, fontSize: 13, fontWeight: 700, letterSpacing: 3, marginBottom: 12, ...F }}>FAQ</div>
          <h2 className="pm-fade" style={{ fontSize: 36, fontWeight: 900, textTransform: 'uppercase', letterSpacing: -1, margin: 0, ...F }}>
            <span style={{ color: '#fff' }}>DÚVIDAS </span><span style={gradText}>FREQUENTES</span>
          </h2>
        </div>
        <div style={{ maxWidth: 660, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {faqItems.map((item, i) => {
            const isOpen = openFaq === i;
            return (
              <div key={i} className="pm-fade" style={{ border: `1px solid ${isOpen ? 'rgba(0,230,51,0.25)' : 'rgba(255,255,255,0.06)'}`, borderRadius: 10, overflow: 'hidden', transition: 'border-color 0.3s' }}>
                <button onClick={() => toggleFaq(i)} style={{
                  width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '16px 20px', background: isOpen ? '#131313' : '#111', border: 'none',
                  color: '#fff', fontSize: 15, fontWeight: 600, cursor: 'pointer', textAlign: 'left', ...F,
                }}>
                  {item.q}
                  <span style={{
                    width: 26, height: 26, borderRadius: '50%', border: `1px solid ${isOpen ? '#00E633' : 'rgba(255,255,255,0.15)'}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: isOpen ? '#00E633' : 'rgba(255,255,255,0.3)', fontSize: 16, fontWeight: 400,
                    transform: isOpen ? 'rotate(45deg)' : 'rotate(0)', transition: 'all 0.3s', flexShrink: 0, lineHeight: 1,
                  }}>+</span>
                </button>
                <div style={{ maxHeight: isOpen ? 120 : 0, overflow: 'hidden', transition: 'max-height 0.3s ease', background: '#0d0d0d' }}>
                  <p style={{ padding: '12px 20px 16px', margin: 0, color: 'rgba(255,255,255,0.4)', fontSize: 14, lineHeight: 1.6, fontWeight: 400, ...F }}>{item.a}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ══════════════════════════════════════
          CTA FINAL
         ══════════════════════════════════════ */}
      <section style={{ position: 'relative', overflow: 'hidden', borderTop: '1px solid rgba(0,230,51,0.08)', borderBottom: '1px solid rgba(0,230,51,0.08)', padding: '120px 48px', textAlign: 'center', minHeight: 420 }}>
        {/* Neon line top */}
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 1, background: 'linear-gradient(90deg, transparent 0%, rgba(0,230,51,0.6) 30%, rgba(200,255,0,0.6) 70%, transparent 100%)', zIndex: 3 }} />
        {/* Background video */}
        <video ref={ctaVideoRef} autoPlay muted loop playsInline style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 0, opacity: 0.2 }} />
        {/* Overlay */}
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.82)', zIndex: 1 }} />

        {/* Content */}
        <div style={{ position: 'relative', zIndex: 2 }}>
          <h2 className="pm-fade" style={{ fontSize: 64, fontWeight: 900, textTransform: 'uppercase', letterSpacing: -2, lineHeight: 0.95, margin: '0 0 0', ...F }}>
            <span style={{ color: '#fff' }}>TE ESPERAMOS NA MESA.</span>
          </h2>
          <h2 className="pm-fade" style={{ fontSize: 64, fontWeight: 900, textTransform: 'uppercase', letterSpacing: -2, lineHeight: 0.95, margin: '4px 0 16px', ...F }}>
            <span style={gradText}>SEMPRE FOI NOSSO.</span>
          </h2>
          <p className="pm-fade" style={{ ...gradText, fontSize: 18, fontWeight: 600, textAlign: 'center', margin: '16px auto 32px', maxWidth: 500, ...F }}>
            Grátis para começar. Sem cartão. Sem enrolação.
          </p>
          <div className="pm-fade">
            <Link to="/cadastro" className="pm-glow" style={{ display: 'inline-block', background: GRAD_CTA, color: '#000', padding: '16px 40px', borderRadius: 10, fontWeight: 800, fontSize: 16, textTransform: 'uppercase', textDecoration: 'none', boxShadow: '0 0 24px rgba(0,230,51,0.25), 0 0 48px rgba(200,255,0,0.1)', ...F }}>CRIAR MINHA CONTA GRÁTIS →</Link>
          </div>
          <p className="pm-fade" style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12, marginTop: 16, letterSpacing: 0.3, fontWeight: 400, ...F }}>Sem cartão de crédito · Cancele quando quiser · Em português</p>
        </div>
      </section>

      {/* ══════════════════════════════════════
          FOOTER
         ══════════════════════════════════════ */}
      <footer style={{ background: '#000', borderTop: '1px solid rgba(255,255,255,0.05)', padding: '48px 48px 32px', position: 'relative', overflow: 'hidden' }}>
        {/* Logo centered */}
        <div style={{ position: 'relative', zIndex: 1, textAlign: 'center', marginBottom: 28 }}>
          <Link to="/" onClick={handleLogoClick} style={{ textDecoration: 'none', display: 'inline-block' }}>
            <img src={logoImg} alt="PokerMind Brasil" style={{ width: 180, opacity: 0.9, objectFit: 'contain' }} />
          </Link>
          <p style={{ fontSize: 13, fontWeight: 400, color: 'rgba(255,255,255,0.3)', marginTop: 10, ...F }}>Feito para quem leva o poker a sério.</p>
        </div>

        {/* Links centered */}
        <div style={{ position: 'relative', zIndex: 1, display: 'flex', justifyContent: 'center', gap: 32, marginBottom: 28, flexWrap: 'wrap' }}>
          {[
            { label: 'Planos', href: '#planos' },
            { label: 'Termos', to: '/termos' },
            { label: 'Privacidade', to: '/privacidade' },
            { label: 'Suporte', to: '/contato' },
            { label: 'Instagram', href: 'https://www.instagram.com/pokermindbrasil' },
            { label: 'Discord', href: '#' },
          ].map(l => l.to ? (
            <Link key={l.label} to={l.to} style={{ color: 'rgba(255,255,255,0.35)', fontSize: 13, fontWeight: 500, textDecoration: 'none', transition: 'color 0.2s', ...F }} onMouseEnter={e => e.currentTarget.style.color = 'rgba(255,255,255,0.7)'} onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.35)'}>{l.label}</Link>
          ) : (
            <a key={l.label} href={l.href} target={l.href.startsWith('http') ? '_blank' : undefined} rel={l.href.startsWith('http') ? 'noopener noreferrer' : undefined} style={{ color: 'rgba(255,255,255,0.35)', fontSize: 13, fontWeight: 500, textDecoration: 'none', transition: 'color 0.2s', ...F }} onMouseEnter={e => e.currentTarget.style.color = 'rgba(255,255,255,0.7)'} onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.35)'}>{l.label}</a>
          ))}
        </div>

        {/* Copyright */}
        <div style={{ position: 'relative', zIndex: 1, borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: 20, textAlign: 'center' }}>
          <span style={{ color: 'rgba(255,255,255,0.2)', fontSize: 12, fontWeight: 400, ...F }}>© 2026 PokerMind Brasil</span>
        </div>
      </footer>
    </div>
  );
}

export default Home;
